import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { forkJoin } from 'rxjs';
import { SharedModule } from '../../../../shared/shared.module';
import {
  NavigationItem,
  navigationItemsInvoices,
} from '../../../../models/navigation-item-model';
import { LabelService } from '../../../../services/label.service';
import { classes, pages } from '../../../../shared/labels';
import {
  StsExpense,
  StsExpenseApiLine,
  StsPrivateClient,
  TipologiaCassaCode,
  TipologiaCassaDescription,
} from '../sts-models';
import { DoctorService } from '../../../../services/doctor.service';
import { InvoicesService } from '../../../../services/invoices.service';
import { ErrorPanelService } from '../../../../services/error-panel.service';
import { FiscalProfileOutput } from '../../../../models/doctor-model';
import { StsExpenseService } from '../sts-expense.service';
import { StsInvoiceCalculationsService } from '../sts-invoice-calculations.service';

export enum IvaOption {
  IVA_22 = '22%',
  IVA_10 = '10%',
  IVA_5 = '5%',
  IVA_4 = '4%',
  IVA_0_N1 = '0% - N1',
  IVA_0_N2_1 = '0% - N2.1',
  IVA_0_N2_2 = '0% - N2.2',
  IVA_0_N4 = '0% - N4',
}

export enum IvaOptionWithDescription {
  IVA_22 = '22%',
  IVA_10 = '10%',
  IVA_5 = '5%',
  IVA_4 = '4%',
  IVA_0_N1 = '0% - N1: escluse ex art.15',
  IVA_0_N2_1 = '0% - N2.1: non soggette ad IVA ai sensi degli artt. da 7 a 7-septies del DPR 633/72',
  IVA_0_N2_2 = '0% - N2.2: non soggette - altri casi',
  IVA_0_N4 = '0% - N4: esenti',
}

const BOLLO_THRESHOLD = 77.47;
const BOLLO_DESCRIPTION_PREFIX = 'Imposta di Bollo';

/** View-model row for `@for`: stable `trackId` and current `lineIndex` in the FormArray. */
interface DocumentLineRow {
  trackId: number;
  lineIndex: number;
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-create-modify-expense-sts',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
  ],
  templateUrl: './create-modify-expense-sts.component.html',
  styleUrl: './create-modify-expense-sts.component.scss',
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class CreateModifyExpenseStsComponent implements OnInit {
  navigationItems: NavigationItem[];
  invoiceForm!: FormGroup;
  isLoading = true;
  readonly invoiceTipo = 'Parcella Privati';
  readonly displayedColumns = [
    'descrizione',
    'quantita',
    'importo €',
    'iva',
    'totale',
    '',
  ];
  readonly tipologiaCassaOptions = Object.entries(
    TipologiaCassaDescription
  ).map(([code, description]) => ({
    code,
    description,
  }));
  readonly ivaOptions = Object.values(IvaOptionWithDescription);

  privateClients: StsPrivateClient[] = [];
  filteredPrivateClients: StsPrivateClient[] = [];
  doctorFiscalProfile!: FiscalProfileOutput;
  defaultIvaOption = IvaOption.IVA_0_N1;
  defaultIvaBollo = IvaOption.IVA_0_N1;
  bankNameOptions: string[] = [];
  bankIbanOptions: string[] = [];
  regularLines: DocumentLineRow[] = [];
  editingInvoiceId: string | null = null;
  private nextLineTrackId = 0;

  get isEditMode(): boolean {
    return this.editingInvoiceId !== null;
  }

  constructor(
    public labelService: LabelService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private doctorService: DoctorService,
    private invoicesService: InvoicesService,
    private errorPanelService: ErrorPanelService,
    private draftService: StsExpenseService,
    private stsCalculationsService: StsInvoiceCalculationsService
  ) {
    this.navigationItems = navigationItemsInvoices.map((item) => {
      if (item.label === pages.invoices_list) {
        return { ...item, class: classes.hnb };
      }
      return { ...item };
    });
  }

  /** Initializes the invoice form and loads data (create or edit). */
  ngOnInit(): void {
    const routeInvoiceId = this.route.snapshot.paramMap.get('invoiceId');

    if (routeInvoiceId) {
      this.editingInvoiceId = routeInvoiceId;
      if (
        this.draftService.hasDraft() &&
        this.draftService.invoiceForm &&
        this.draftService.editingInvoiceId === routeInvoiceId
      ) {
        this.restoreFromDraft();
        return;
      }
      this.draftService.clear();
      this.editingInvoiceId = routeInvoiceId;
      this.createForm();
      this.loadEditData(routeInvoiceId);
      return;
    }

    if (this.draftService.hasDraft() && this.draftService.invoiceForm) {
      this.restoreFromDraft();
      return;
    }

    this.draftService.clear();
    this.createForm();
    this.loadInitialData();
  }

  /** Builds the reactive form with STS defaults (fixed invoice type, document lines array). */
  private createForm(): void {
    this.invoiceForm = this.fb.group({
      tipo: ['', Validators.required],
      client_id: [null, Validators.required],
      cliente: [null, Validators.required],
      riferimento: [null, [Validators.maxLength(200)]],
      progressivo_numero: [
        null,
        [Validators.required, Validators.pattern(/^\d{3}$/)],
      ],
      progressivo_anno: [null, Validators.required],
      data: [null, Validators.required],
      pagata: [false],
      scadenza: [null, Validators.required],
      pagamento: [null, Validators.required],
      cassa_previdenza: [true],
      tipologia_cassa: [null],
      cassa_previdenza_codice_iva: [null],
      percentuale_cassa: [null],
      applica_bollo: [false],
      escludi_bollo: [false],
      imponibile: [{ value: null, disabled: true }],
      totale_cassa_previdenza: [{ value: null, disabled: true }],
      imposta: [{ value: 0, disabled: true }],
      bollo: [{ value: null, disabled: true }],
      totale: [{ value: null, disabled: true }],
      totale_ritenuta: [{ value: null, disabled: true }],
      linee_documento: this.fb.array([]),
      doctor_bank_name: [null],
      doctor_bank_iban: [null],
      flag_opposizione: [false],
    });

    this.invoiceForm.patchValue({ tipo: this.invoiceTipo });
    this.invoiceForm.get('tipo')?.disable();
  }

  /** Fetches fiscal profile, private clients, and next progressive number in parallel. */
  private loadInitialData(): void {
    this.isLoading = true;

    forkJoin({
      fiscalProfile: this.doctorService.getFiscalProfile(),
      privateClients: this.invoicesService.getInvoicesPrivateClients(),
      nextNumber: this.invoicesService.getSTSNextNumber(),
    }).subscribe({
      next: ({ fiscalProfile, privateClients, nextNumber }) => {
        this.applyFiscalProfile(fiscalProfile);
        this.applyPrivateClients(privateClients);
        this.applyNextNumber(nextNumber);
        this.addDocumentLine();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading initial STS invoice data:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento dei dati per la nuova spesa STS'
        );
        this.isLoading = false;
      },
    });
  }

  /**
   * Stores the doctor fiscal profile, sets default VAT options by tax regime,
   * bank/IBAN autocomplete options, and previdenza defaults on the form.
   */
  private applyFiscalProfile(profile: FiscalProfileOutput): void {
    this.doctorFiscalProfile = profile;
    switch (this.doctorFiscalProfile.regime_fiscale) {
      case 'RF01':
        this.defaultIvaOption = IvaOption.IVA_0_N4;
        this.defaultIvaBollo = IvaOption.IVA_0_N1;
        break;
      case 'RF02':
      case 'RF19':
        this.defaultIvaOption = IvaOption.IVA_0_N2_2;
        this.defaultIvaBollo = IvaOption.IVA_0_N2_2;
        break;
    }

    this.applyBankAndIbanOptions(profile);

    if (!this.isEditMode) {
      this.invoiceForm.patchValue({
        tipologia_cassa: TipologiaCassaCode.TC21,
        percentuale_cassa: '2',
        cassa_previdenza_codice_iva: this.defaultIvaOption,
      });
    }
  }

  /** Sorts private clients by last name and exposes them for autocomplete. */
  private applyPrivateClients(clients: StsPrivateClient[]): void {
    this.privateClients = clients.sort((a, b) =>
      a.client_last_name.localeCompare(b.client_last_name)
    );
    this.filteredPrivateClients = this.privateClients;
  }

  /**
   * Loads fiscal profile, clients, next number, and existing invoice in parallel
   * for the edit page (`/fatture/sts/:invoiceId`).
   */
  private loadEditData(invoiceId: string): void {
    this.isLoading = true;

    forkJoin({
      fiscalProfile: this.doctorService.getFiscalProfile(),
      privateClients: this.invoicesService.getInvoicesPrivateClients(),
      nextNumber: this.invoicesService.getSTSNextNumber(),
      invoice: this.invoicesService.getSTSInvoiceById(invoiceId),
    }).subscribe({
      next: ({ fiscalProfile, privateClients, invoice }) => {
        this.applyFiscalProfile(fiscalProfile);
        this.applyPrivateClients(privateClients);
        this.populateFormFromInvoice(invoice);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading STS invoice for edit:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante il caricamento della spesa STS'
        );
        this.isLoading = false;
        this.router.navigate(['/fatture']);
      },
    });
  }

  /** Maps API document line VAT fields to the form `iva_option` select value. */
  private toIvaOption(line: StsExpenseApiLine): string {
    if (line.natura_iva) {
      return `0% - ${line.natura_iva}`;
    }
    return `${line.iva}%`;
  }

  private resolveClientName(clientId: string): string | null {
    const client = this.privateClients.find((c) => c.id === clientId);
    if (!client) {
      return null;
    }
    return `${client.client_first_name} ${client.client_last_name}`;
  }

  private isBolloLineFromApi(line: StsExpenseApiLine): boolean {
    if (line.is_bollo) {
      return true;
    }
    return line.descrizione?.startsWith(BOLLO_DESCRIPTION_PREFIX) ?? false;
  }

  /** Fills the form from `GET /invoicing/sts/:id` (edit mode). */
  private populateFormFromInvoice(invoice: StsExpense): void {
    const formattedDate = invoice.data
      ? moment(invoice.data, 'DD/MM/YYYY').toDate()
      : null;

    let cassa_previdenza_codice_iva: string | null = null;
    if (invoice.cassa_previdenza_natura_iva) {
      cassa_previdenza_codice_iva = `0% - ${invoice.cassa_previdenza_natura_iva}`;
    } else if (invoice.cassa_previdenza_iva != null) {
      cassa_previdenza_codice_iva = `${invoice.cassa_previdenza_iva}%`;
    }

    this.invoiceForm.patchValue({
      tipo: invoice.tipo || this.invoiceTipo,
      client_id: invoice.client_id || null,
      cliente: this.resolveClientName(invoice.client_id),
      riferimento: invoice.riferimento || null,
      progressivo_numero: invoice.progressivo_numero || null,
      progressivo_anno: invoice.progressivo_anno || null,
      data: formattedDate,
      pagata: invoice.pagata ?? false,
      scadenza: invoice.scadenza || null,
      pagamento: invoice.pagamento || null,
      cassa_previdenza: invoice.cassa_previdenza ?? false,
      tipologia_cassa: invoice.tipologia_cassa || null,
      percentuale_cassa: invoice.percentuale_cassa ?? null,
      cassa_previdenza_codice_iva,
      applica_bollo: invoice.applica_bollo ?? false,
      escludi_bollo: invoice.escludi_bollo ?? false,
      doctor_bank_name: invoice.doctor_bank_name || null,
      doctor_bank_iban: invoice.doctor_bank_iban || null,
      flag_opposizione: invoice.flag_opposizione ?? false,
    });

    while (this.documentLinesFormArray.length) {
      this.documentLinesFormArray.removeAt(0);
    }
    this.nextLineTrackId = 0;

    if (invoice.linee_documento?.length) {
      invoice.linee_documento.forEach((line) => {
        if (this.isBolloLineFromApi(line)) {
          const bolloLine = this.createBolloLine(
            line.descrizione || BOLLO_DESCRIPTION_PREFIX
          );
          bolloLine.get('iva_option')?.setValue(this.toIvaOption(line));
          bolloLine
            .get('totale')
            ?.setValue(Number(line.totale).toFixed(2), { emitEvent: false });
          this.documentLinesFormArray.push(bolloLine);
          this.setupLineSubscriptions(bolloLine, false);
          return;
        }

        const lineGroup = this.createDocumentLine();
        lineGroup.patchValue({
          descrizione: line.descrizione || '',
          quantita: String(line.quantita ?? '1'),
          prezzo: Number(line.prezzo).toFixed(2),
          iva_option: this.toIvaOption(line),
        });
        this.documentLinesFormArray.push(lineGroup);
        this.setupLineSubscriptions(lineGroup, false);
      });
    } else {
      this.addDocumentLine();
    }

    this.refreshRegularLinesView();
    this.updateInvoiceTotals();
    this.invoiceForm.markAsPristine();
  }

  /** Patches progressive number and year from the STS next-number API. */
  private applyNextNumber(nextNumber: {
    progressivo_numero: string;
    progressivo_anno: number;
  }): void {
    this.invoiceForm
      .get('progressivo_numero')
      ?.setValue(nextNumber.progressivo_numero);
    this.invoiceForm
      .get('progressivo_anno')
      ?.setValue(nextNumber.progressivo_anno);
  }

  /** Shorthand accessor for the `linee_documento` FormArray. */
  get documentLinesFormArray(): FormArray {
    return this.invoiceForm.get('linee_documento') as FormArray;
  }

  /** True when payment method is bank transfer (shows bank/IBAN fields). */
  get isBonifico(): boolean {
    return this.invoiceForm?.get('pagamento')?.value === 'Bonifico';
  }

  /** Index of the stamp-duty line in the document lines array, or -1 if absent. */
  get bolloLineIndex(): number {
    return this.documentLinesFormArray.controls.findIndex((_, index) =>
      this.isBolloLineIndex(index)
    );
  }

  /** FormGroup for the stamp-duty row, bound separately below regular lines in the template. */
  get bolloLineGroup(): FormGroup | null {
    const index = this.bolloLineIndex;
    if (index === -1) {
      return null;
    }
    return this.documentLinesFormArray.at(index) as FormGroup;
  }

  /** Returns the document line FormGroup at the given index in the FormArray. */
  getLineGroup(lineIndex: number): FormGroup {
    return this.documentLinesFormArray.at(lineIndex) as FormGroup;
  }

  /** Segna la descrizione come toccata al click, per mostrare l'errore solo dopo interazione. */
  markLineDescrizioneTouched(lineIndex: number): void {
    this.getLineGroup(lineIndex).get('descrizione')?.markAsTouched();
  }

  markBolloDescrizioneTouched(): void {
    this.bolloLineGroup?.get('descrizione')?.markAsTouched();
  }

  /**
   * Whether the row at the given index is the automatic stamp-duty line
   * (uses `is_bollo`, not description which may be disabled in the form).
   */
  isBolloLineIndex(index: number): boolean {
    const line = this.documentLinesFormArray.at(index);
    if (!line) {
      return false;
    }
    return line.get('is_bollo')?.value === true;
  }

  /**
   * Rebuilds `regularLines` for the template: non-bollo rows only, with
   * `lineIndex` refreshed after inserts/removals in the FormArray.
   */
  private refreshRegularLinesView(): void {
    this.regularLines = this.documentLinesFormArray.controls
      .map((control, lineIndex) => ({
        lineIndex,
        trackId:
          (control as FormGroup & { __trackId?: number }).__trackId ??
          lineIndex,
        isBollo: control.get('is_bollo')?.value === true,
      }))
      .filter((row) => !row.isBollo)
      .map(({ lineIndex, trackId }) => ({ lineIndex, trackId }));
  }

  /**
   * Resolves the current FormArray index for a row by its stable `trackId`
   * (unchanged when other rows are removed above it).
   */
  private findLineIndexByTrackId(trackId: number): number {
    return this.documentLinesFormArray.controls.findIndex(
      (control) =>
        (control as FormGroup & { __trackId?: number }).__trackId === trackId
    );
  }

  /** Assigns an incremental id on the FormGroup for `@for` `track row.trackId`. */
  private assignLineTrackId(lineGroup: FormGroup): void {
    (lineGroup as FormGroup & { __trackId: number }).__trackId = ++this
      .nextLineTrackId;
  }

  /** Creates an empty editable document line with default VAT and validators. */
  private createDocumentLine(): FormGroup {
    const lineGroup = this.fb.group({
      is_bollo: [false],
      descrizione: [
        '',
        [
          Validators.required,
          Validators.minLength(1),
          Validators.maxLength(1000),
        ],
      ],
      quantita: ['1.00'],
      prezzo: [
        '0.00',
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(/^\d*\.?\d{0,2}$/),
        ],
      ],
      iva_option: [this.defaultIvaOption],
      totale: [{ value: '0.00', disabled: true }],
    });
    this.assignLineTrackId(lineGroup);
    return lineGroup;
  }

  private bolloDescrizionePrefixValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) {
        return { required: true };
      }
      if (!value.startsWith(BOLLO_DESCRIPTION_PREFIX)) {
        return { bolloDescrizionePrefix: true };
      }
      return null;
    };
  }

  /** Creates the fixed €2 stamp-duty line (locked fields, regime-specific VAT). */
  private createBolloLine(
    descrizione: string = BOLLO_DESCRIPTION_PREFIX
  ): FormGroup {
    const lineGroup = this.fb.group({
      is_bollo: [true],
      descrizione: [
        descrizione,
        [
          Validators.required,
          Validators.maxLength(1000),
          this.bolloDescrizionePrefixValidator(),
        ],
      ],
      quantita: [{ value: '1.00', disabled: true }],
      prezzo: [{ value: '2.00', disabled: true }],
      iva_option: [{ value: this.defaultIvaBollo, disabled: false }],
      totale: [{ value: '2.00', disabled: true }],
    });
    this.assignLineTrackId(lineGroup);
    return lineGroup;
  }

  /** Computes line total as quantity × unit price. */
  private calculateLineTotal(lineGroup: FormGroup): number {
    const quantita = parseFloat(lineGroup.get('quantita')?.value || '0');
    const prezzo = parseFloat(lineGroup.get('prezzo')?.value || '0');
    return quantita * prezzo;
  }

  /** Writes the computed total into the disabled `totale` control. */
  private updateLineTotal(lineGroup: FormGroup): void {
    const totale = this.calculateLineTotal(lineGroup);
    lineGroup.get('totale')?.setValue(totale.toFixed(2), { emitEvent: false });
  }

  /**
   * Subscribes to quantity, price, and VAT changes to refresh totals;
   * optionally re-runs stamp-duty sync when `syncBollo` is true.
   */
  private setupLineSubscriptions(
    lineGroup: FormGroup,
    syncBollo: boolean
  ): void {
    const onChange = () => {
      this.updateLineTotal(lineGroup);
      if (syncBollo) {
        this.syncBolloLine();
      }
    };

    ['quantita', 'prezzo', 'iva_option'].forEach((field) => {
      lineGroup.get(field)?.valueChanges.subscribe(() => onChange());
    });

    onChange();
  }

  /**
   * Numeric line total: prefers `getRawValue().totale` (includes disabled controls),
   * falls back to quantity × price.
   */
  private getLineTotalValue(line: FormGroup): number {
    const totaleFromRaw = parseFloat(line.getRawValue().totale ?? '');
    if (!Number.isNaN(totaleFromRaw) && totaleFromRaw > 0) {
      return totaleFromRaw;
    }
    return this.calculateLineTotal(line);
  }

  /**
   * Sum of totals for non-bollo lines with 0% VAT (N1/N2/N4 options),
   * used to decide whether stamp duty applies (> €77.47).
   */
  private getExemptLinesTotalWithoutBollo(): number {
    return this.documentLinesFormArray.controls.reduce(
      (sum, control, index) => {
        if (this.isBolloLineIndex(index)) {
          return sum;
        }
        const line = control as FormGroup;
        const ivaOption = String(line.get('iva_option')?.value ?? '');
        if (!ivaOption.includes('0% -')) {
          return sum;
        }
        return sum + this.getLineTotalValue(line);
      },
      0
    );
  }

  /**
   * Adds or removes the stamp-duty row when exempt-line total crosses the threshold;
   * updates `applica_bollo` and refreshes the regular-lines view.
   */
  private syncBolloLine(): void {
    const exemptTotal = this.getExemptLinesTotalWithoutBollo();
    const shouldApplyBollo = exemptTotal > BOLLO_THRESHOLD;
    const bolloIndex = this.bolloLineIndex;

    this.invoiceForm
      .get('applica_bollo')
      ?.setValue(shouldApplyBollo, { emitEvent: false });

    if (shouldApplyBollo && bolloIndex === -1) {
      const bolloLine = this.createBolloLine();
      this.documentLinesFormArray.push(bolloLine);
      this.setupLineSubscriptions(bolloLine, false);
    } else if (!shouldApplyBollo && bolloIndex !== -1) {
      this.documentLinesFormArray.removeAt(bolloIndex);
    }

    this.refreshRegularLinesView();
  }

  /** Appends a new line before the bollo row (if present) and wires change handlers. */
  addDocumentLine(): void {
    const lineGroup = this.createDocumentLine();
    const bolloIndex = this.bolloLineIndex;

    if (bolloIndex === -1) {
      this.documentLinesFormArray.push(lineGroup);
    } else {
      this.documentLinesFormArray.insert(bolloIndex, lineGroup);
    }

    this.setupLineSubscriptions(lineGroup, true);
    this.refreshRegularLinesView();
  }

  /**
   * Removes a user line by stable track id (ignores bollo and missing rows),
   * then re-syncs stamp duty.
   */
  removeDocumentLineByTrackId(trackId: number): void {
    const lineIndex = this.findLineIndexByTrackId(trackId);
    if (lineIndex === -1 || this.isBolloLineIndex(lineIndex)) {
      return;
    }

    this.documentLinesFormArray.removeAt(lineIndex);
    this.syncBolloLine();
  }

  /** Builds Banca/IBAN mat-select options from the fiscal profile. */
  private applyBankAndIbanOptions(profile: FiscalProfileOutput): void {
    this.bankNameOptions = [];
    if (profile.bank_name) {
      this.bankNameOptions.push(profile.bank_name);
    }
    if (profile.bank_name_2) {
      this.bankNameOptions.push(profile.bank_name_2);
    }

    this.bankIbanOptions = [];
    if (profile.iban) {
      this.bankIbanOptions.push(profile.iban);
    }
    if (profile.iban_2) {
      this.bankIbanOptions.push(profile.iban_2);
    }
  }

  /** Restores form state when returning from the review step. */
  private restoreFromDraft(): void {
    this.invoiceForm = this.draftService.invoiceForm!;
    this.doctorFiscalProfile = this.draftService.doctorFiscalProfile!;
    this.privateClients = this.draftService.privateClients;
    this.filteredPrivateClients = this.privateClients;
    this.nextLineTrackId = this.draftService.nextLineTrackId;
    this.editingInvoiceId = this.draftService.editingInvoiceId;
    this.applyBankAndIbanOptions(this.doctorFiscalProfile);
    this.isLoading = false;
    this.refreshRegularLinesView();
    this.updateInvoiceTotals();
  }

  /** Recomputes invoice totals for the STS review step. */
  private updateInvoiceTotals(): void {
    this.stsCalculationsService.updateFormWithCalculatedTotals(
      this.documentLinesFormArray,
      this.invoiceForm,
      this.doctorFiscalProfile?.regime_fiscale ?? undefined
    );
  }

  showAllClients(): void {
    this.filteredPrivateClients = this.privateClients;
  }

  filterClients(event: Event): void {
    if (this.invoiceForm.get('client_id')?.value !== null) {
      this.invoiceForm.get('client_id')?.patchValue(null);
    }
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredPrivateClients = this.privateClients.filter((client) =>
      `${client.client_first_name} ${client.client_last_name}`
        .toLowerCase()
        .includes(searchTerm)
    );
  }

  selectClient(
    client: StsPrivateClient,
    event: { isUserInput?: boolean }
  ): void {
    if (event.isUserInput === false) {
      return;
    }
    this.invoiceForm.patchValue({
      client_id: client.id,
      cliente: `${client.client_first_name} ${client.client_last_name}`,
    });
  }

  onClientBlur(): void {
    if (this.invoiceForm.get('client_id')?.value === null) {
      this.invoiceForm.get('cliente')?.patchValue(null);
    }
  }

  /** Validates the form, saves draft state, and navigates to the review page. */
  onContinue(): void {
    this.syncBolloLine();
    this.updateInvoiceTotals();
    this.stsCalculationsService.markFormGroupTouched(this.invoiceForm);
    this.documentLinesFormArray.controls.forEach((control) => {
      this.stsCalculationsService.markFormGroupTouched(control as FormGroup);
    });

    if (!this.invoiceForm.valid) {
      this.errorPanelService.showErrorSnackBar(
        'Compila tutti i campi obbligatori prima di continuare'
      );
      return;
    }

    this.draftService.saveDraft(
      this.invoiceForm,
      this.doctorFiscalProfile,
      this.privateClients,
      this.nextLineTrackId,
      this.editingInvoiceId
    );
    this.router.navigate(['/fatture/sts/riepilogo']);
  }

  /** Navigates back to the invoices list without saving. */
  onCancel(): void {
    this.draftService.clear();
    this.router.navigate(['/fatture']);
  }
}
